// The granular permission schema — modeled on the IG Field Suite's
// permissionSchema/effectivePermissions design, adapted to Flowio's
// multi-tenant worker.
//
// Structure: MODULE → SECTION → the CRUD actions that section really has.
// A permission is addressed by its key "module.section.ACTION". Resolution
// layers, highest precedence first:
//   1. per-member overrides   (workspace_member.perms_json — one person's
//                              exceptions, e.g. "Lauren also runs sync")
//   2. per-role overrides     (permission_override rows — the agency's
//                              Settings → Permissions grid)
//   3. built-in role defaults (getDefault below; custom roles inherit the
//                              defaults of the built-in they're based on)
// Owner short-circuits to allow-everything and is never editable.
//
// Lockout guard: the ADMIN role can never lose team management or
// integrations settings — the two levers needed to undo a bad edit.

export const ACTIONS = ["CREATE", "READ", "UPDATE", "DELETE"] as const;
export type PermAction = (typeof ACTIONS)[number];

export type SectionDef = { key: string; label: string; actions: PermAction[]; desc?: string };
export type ModuleDef = { module: string; label: string; sections: SectionDef[] };

export const PERMISSION_MODULES: ModuleDef[] = [
  {
    module: "accounts", label: "Accounts & Pipeline",
    sections: [
      { key: "data",        label: "Account records",     actions: ["READ", "UPDATE", "DELETE"], desc: "Pipeline, account pages, search" },
      { key: "create",      label: "Create accounts",     actions: ["CREATE"] },
      { key: "notes",       label: "Activity notes",      actions: ["CREATE"] },
      { key: "attachments", label: "File attachments",    actions: ["CREATE", "READ", "DELETE"] },
      { key: "ghl",         label: "GHL writeback",       actions: ["UPDATE"], desc: "Push fields to GHL, per-account activity sync" },
    ],
  },
  {
    module: "bookings", label: "Bookings",
    sections: [
      { key: "data",    label: "Booking records",  actions: ["CREATE", "READ", "UPDATE", "DELETE"] },
      { key: "grading", label: "Grade & confirm",  actions: ["UPDATE"], desc: "Grade, confirm, mark showed" },
    ],
  },
  {
    module: "calls", label: "Sales Calls",
    sections: [
      { key: "data", label: "Call log", actions: ["CREATE", "READ", "UPDATE", "DELETE"], desc: "Incl. cash / MRR on outcomes" },
    ],
  },
  {
    module: "setter", label: "Setter Stats",
    sections: [
      { key: "own",  label: "Own daily numbers",   actions: ["READ", "UPDATE"] },
      { key: "team", label: "Whole team's numbers", actions: ["READ"] },
    ],
  },
  {
    module: "financials", label: "Agency Financials",
    sections: [
      { key: "mrr",       label: "MRR & subscriptions", actions: ["READ"] },
      { key: "metrics",   label: "Money metrics",       actions: ["READ"], desc: "Cash, cost-per, MRR tiles on dashboards" },
      { key: "scoreboard", label: "Scoreboard & reports", actions: ["CREATE", "READ"] },
    ],
  },
  {
    module: "contracts", label: "Contracts",
    sections: [
      { key: "builder", label: "Contract Builder", actions: ["READ"] },
      { key: "intake",  label: "Contract intake answers", actions: ["READ"], desc: "Portal form submissions" },
    ],
  },
  {
    module: "checklists", label: "Fulfillment Checklists",
    sections: [
      { key: "customer", label: "Customer checklists", actions: ["CREATE", "READ", "UPDATE", "DELETE"] },
      { key: "secrets",  label: "Customer logins (passwords)", actions: ["READ", "DELETE"], desc: "Reveal a login a customer stored in their portal (every reveal is logged) and delete it when the job is done" },
    ],
  },
  {
    module: "portal", label: "Client Portal",
    sections: [
      { key: "contacts",    label: "Portal contacts",   actions: ["CREATE", "READ", "DELETE"] },
      { key: "links",       label: "Magic sign-in links", actions: ["CREATE"] },
      { key: "forms",       label: "Portal forms",      actions: ["CREATE", "READ", "UPDATE", "DELETE"] },
      { key: "submissions", label: "Submissions",       actions: ["READ"] },
    ],
  },
  {
    module: "tickets", label: "Support Tickets",
    sections: [
      { key: "data",  label: "Tickets assigned to you", actions: ["CREATE", "READ", "UPDATE", "DELETE"], desc: "Open, reply to and move tickets assigned to them" },
      { key: "all",   label: "Every ticket",            actions: ["READ", "UPDATE"], desc: "See and work tickets assigned to anyone, plus the unassigned queue" },
      { key: "types", label: "Ticket types & turnaround", actions: ["CREATE", "UPDATE", "DELETE"] },
    ],
  },
  {
    module: "templates", label: "Message Templates",
    sections: [
      { key: "use",  label: "Use templates",  actions: ["READ"], desc: "List and render with account data" },
      { key: "data", label: "Edit templates", actions: ["CREATE", "UPDATE", "DELETE"] },
    ],
  },
  {
    module: "content", label: "Content & Wiki",
    sections: [
      { key: "wiki",                label: "Wiki spaces & pages",  actions: ["CREATE", "UPDATE", "DELETE"] },
      { key: "blocks",              label: "Content blocks",       actions: ["CREATE", "UPDATE", "DELETE"] },
      { key: "checklist-templates", label: "Checklist templates",  actions: ["CREATE", "UPDATE", "DELETE"] },
      { key: "fields",              label: "Field configuration",  actions: ["CREATE", "UPDATE", "DELETE"] },
    ],
  },
  {
    module: "videos", label: "VSL Hosting",
    sections: [
      { key: "library", label: "Video library",     actions: ["READ"] },
      { key: "manage",  label: "Upload & edit",     actions: ["CREATE", "UPDATE", "DELETE"] },
      { key: "heygen",  label: "HeyGen generation", actions: ["CREATE"], desc: "Spends the HeyGen API key" },
    ],
  },
  {
    module: "ai", label: "AI Tools",
    sections: [
      { key: "research", label: "Deep research",  actions: ["CREATE"], desc: "Most expensive — spends LLM keys" },
      { key: "drafts",   label: "Summaries & drafts", actions: ["CREATE"] },
      { key: "fathom",   label: "Fathom meetings", actions: ["CREATE", "UPDATE"], desc: "Sync and attach recordings" },
    ],
  },
  {
    module: "agents", label: "Agents",
    sections: [
      { key: "data", label: "Agents & run log", actions: ["CREATE", "READ", "UPDATE", "DELETE"], desc: "Create and edit agents; read their run log" },
      { key: "run",  label: "Run agents",       actions: ["CREATE"], desc: "Chat with an agent — spends the agency's AI key (Gemini free tier by default)" },
      { key: "approve", label: "Approve agent actions", actions: ["UPDATE"], desc: "Approve or reject what agents want to send to clients (texts, emails). Owners and admins by default" },
    ],
  },
  {
    module: "audits", label: "Audit Reports",
    sections: [
      { key: "data", label: "Audit reports", actions: ["CREATE", "READ", "DELETE"], desc: "Pull/generate uses GHL + Places APIs" },
    ],
  },
  {
    module: "websites", label: "Website Builder",
    sections: [
      { key: "build", label: "Build & deploy", actions: ["CREATE", "READ"], desc: "Turns audit + research into a site; spends the LLM key and deploys to Cloudflare" },
    ],
  },
  {
    module: "sync", label: "GHL Sync",
    sections: [
      { key: "run",      label: "Full sync",        actions: ["CREATE"] },
      { key: "calendar", label: "Calendar config",  actions: ["UPDATE"] },
      { key: "reset",    label: "Reset live data",  actions: ["DELETE"] },
    ],
  },
  {
    module: "settings", label: "Settings & Admin",
    sections: [
      { key: "integrations", label: "Integrations & API keys", actions: ["UPDATE"] },
      { key: "agency",       label: "Agency content settings", actions: ["UPDATE"] },
      { key: "automations",  label: "Automations",             actions: ["CREATE", "UPDATE", "DELETE"] },
      { key: "data-admin",   label: "Data Admin grid",         actions: ["CREATE", "READ", "UPDATE", "DELETE"] },
    ],
  },
  {
    module: "team", label: "Team",
    sections: [
      { key: "members", label: "Members",  actions: ["CREATE", "READ", "UPDATE", "DELETE"] },
      { key: "invites", label: "Invites",  actions: ["CREATE", "READ", "DELETE"] },
    ],
  },
];

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export type BuiltinRole = "owner" | "admin" | "closer" | "setter" | "fulfillment";
export const BUILTIN_ROLES: BuiltinRole[] = ["owner", "admin", "closer", "setter", "fulfillment"];

export const ROLE_LABEL: Record<BuiltinRole, string> = {
  owner: "Owner", admin: "Admin", closer: "Closer", setter: "Setter", fulfillment: "Fulfillment",
};
export const ROLE_BLURB: Record<BuiltinRole, string> = {
  owner: "Everything, including deleting the agency and granting owner.",
  admin: "Everything operational: settings, integrations, sync, team, content.",
  closer: "Accounts, calls and bookings; own stats; no agency financials.",
  setter: "Pipeline, dial flow and bookings; audits; no call outcomes or financials.",
  fulfillment: "Read accounts, run checklists, manage the client portal.",
};

export type RoleDef = {
  name: string;          // slug, e.g. "junior-setter"
  label: string;
  description?: string | null;
  builtin: boolean;
  based_on?: string | null; // builtin role whose defaults a custom role inherits
  sort_order?: number;
};

export function normalizeBuiltin(r: string | null | undefined): BuiltinRole | null {
  if (r === "owner" || r === "admin" || r === "closer" || r === "setter" || r === "fulfillment") return r;
  if (r === "member") return "closer"; // legacy
  return null;
}

// The builtin a role's DEFAULTS come from: itself for builtins, based_on for
// custom roles, "setter" (least privileged staff) when unknown.
export function defaultsBase(role: string | null | undefined, roleDefs?: RoleDef[] | null): BuiltinRole {
  const b = normalizeBuiltin(role);
  if (b) return b;
  const def = roleDefs?.find((d) => d.name === role);
  return normalizeBuiltin(def?.based_on) ?? "setter";
}

// ---------------------------------------------------------------------------
// Built-in defaults. Listed as allow-sets of "module.section.ACTION" keys;
// anything not listed is denied for that role. Owner/admin are allow-all
// (admin minus nothing here — admin restrictions only ever come from
// overrides, and the locked cells below can't even be overridden).
// ---------------------------------------------------------------------------

export const permKey = (module: string, section: string, action: PermAction) =>
  `${module}.${section}.${action}`;

export function allPermKeys(): string[] {
  const out: string[] = [];
  for (const m of PERMISSION_MODULES) for (const s of m.sections) for (const a of s.actions)
    out.push(permKey(m.module, s.key, a));
  return out;
}

const CLOSER_ALLOW = new Set<string>([
  "agents.data.READ", "agents.run.CREATE",
  "accounts.data.READ", "accounts.data.UPDATE", "accounts.create.CREATE",
  "accounts.notes.CREATE", "accounts.attachments.CREATE", "accounts.attachments.READ", "accounts.attachments.DELETE",
  "accounts.ghl.UPDATE",
  "tickets.data.CREATE", "tickets.data.READ", "tickets.data.UPDATE",
  "bookings.data.CREATE", "bookings.data.READ", "bookings.data.UPDATE", "bookings.data.DELETE",
  "bookings.grading.UPDATE",
  "calls.data.CREATE", "calls.data.READ", "calls.data.UPDATE", "calls.data.DELETE",
  "setter.own.READ", "setter.own.UPDATE",
  "contracts.builder.READ", "contracts.intake.READ",
  "templates.use.READ",
  "videos.library.READ",
  "ai.research.CREATE", "ai.drafts.CREATE", "ai.fathom.CREATE", "ai.fathom.UPDATE",
  "audits.data.CREATE", "audits.data.READ",
  "websites.build.CREATE", "websites.build.READ",
]);

const SETTER_ALLOW = new Set<string>([
  "agents.data.READ", "agents.run.CREATE",
  "accounts.data.READ", "accounts.data.UPDATE", "accounts.create.CREATE",
  "accounts.notes.CREATE", "accounts.attachments.CREATE", "accounts.attachments.READ",
  "accounts.ghl.UPDATE",
  "bookings.data.CREATE", "bookings.data.READ", "bookings.data.UPDATE",
  "bookings.grading.UPDATE",
  "setter.own.READ", "setter.own.UPDATE",
  "templates.use.READ",
  "videos.library.READ",
  "audits.data.CREATE", "audits.data.READ",
]);

const FULFILLMENT_ALLOW = new Set<string>([
  "agents.data.READ", "agents.run.CREATE",
  "accounts.data.READ",
  "accounts.notes.CREATE", "accounts.attachments.READ",
  "checklists.customer.CREATE", "checklists.customer.READ", "checklists.customer.UPDATE", "checklists.customer.DELETE",
  "checklists.secrets.READ", "checklists.secrets.DELETE",
  "portal.contacts.CREATE", "portal.contacts.READ", "portal.contacts.DELETE",
  "portal.links.CREATE",
  "portal.forms.CREATE", "portal.forms.READ", "portal.forms.UPDATE", "portal.forms.DELETE",
  "portal.submissions.READ",
  "tickets.data.CREATE", "tickets.data.READ", "tickets.data.UPDATE", "tickets.data.DELETE",
  "tickets.all.READ", "tickets.all.UPDATE",
  "templates.use.READ",
  "videos.library.READ",
]);

export function getDefault(base: BuiltinRole, key: string): boolean {
  switch (base) {
    case "owner":
    case "admin": return true;
    case "closer": return CLOSER_ALLOW.has(key);
    case "setter": return SETTER_ALLOW.has(key);
    case "fulfillment": return FULFILLMENT_ALLOW.has(key);
  }
}

// (role, key) cells that can never be turned off — the undo path.
export function isLockedKey(role: string, key: string): boolean {
  if (role !== "admin") return false;
  return key.startsWith("team.") || key === "settings.integrations.UPDATE";
}

// ---------------------------------------------------------------------------
// Effective resolution
// ---------------------------------------------------------------------------

export type OverrideRow = { module: string; section: string; action: string; allowed: number | boolean };
export type PermMap = Record<string, boolean>;

const VALID_KEYS = new Set(allPermKeys());

// Defaults + role override rows + member exception map → effective map.
export function buildEffective(
  role: string | null | undefined,
  roleDefs: RoleDef[] | null | undefined,
  roleOverrides: OverrideRow[] | null | undefined,
  memberOverrides: Record<string, unknown> | null | undefined
): PermMap {
  const map: PermMap = {};
  if (role === "owner") { for (const k of VALID_KEYS) map[k] = true; return map; }
  const base = defaultsBase(role, roleDefs);
  for (const k of VALID_KEYS) map[k] = getDefault(base, k);
  for (const r of roleOverrides ?? []) {
    const k = permKey(r.module, r.section, r.action as PermAction);
    if (VALID_KEYS.has(k) && !isLockedKey(role ?? "", k)) map[k] = !!Number(r.allowed) || r.allowed === true;
  }
  if (memberOverrides && typeof memberOverrides === "object") {
    for (const [k, v] of Object.entries(memberOverrides)) {
      if (VALID_KEYS.has(k) && typeof v === "boolean" && !isLockedKey(role ?? "", k)) map[k] = v;
    }
  }
  return map;
}

// Sanitize an override diff (role grid save or member exception save):
// unknown keys, locked cells and non-boolean values are dropped; values that
// restate the role's default are dropped so what's stored is a true diff.
export function sanitizeDiff(
  role: string,
  roleDefs: RoleDef[] | null | undefined,
  raw: unknown
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  const base = defaultsBase(role, roleDefs);
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!VALID_KEYS.has(k) || typeof v !== "boolean") continue;
    if (isLockedKey(role, k)) continue;
    if (getDefault(base, k) === v) continue;
    out[k] = v;
  }
  return out;
}

export function isValidPermKey(k: string): boolean { return VALID_KEYS.has(k); }

// Role slugs for custom roles.
export function slugifyRole(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
}
export function isBuiltinRole(name: string): boolean {
  return (BUILTIN_ROLES as string[]).includes(name) || name === "member";
}
